import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import { Entry } from "@/features/giveaways/models/entry";
import {
	GiveawayDetailsRoute,
	HomeRoute,
	MerchRoute,
} from "@/features/home/route";
import { useSiteSettings } from "@/common/providers/site-settings";
import { CartItem } from "@/features/merch/models/cart";
import { useCart } from "@/features/merch/providers/cart";
import { Plan } from "@/features/payments/models/plan";
import {
	ProfileDashboardRoute,
	ProfileManageMembershipRoute,
} from "@/features/profile/route";
import * as ProfileServices from "@/features/profile/services/profiles";
import { formatPrice } from "@/lib/string";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import InlineStripeCheckout, {
	type DefaultAddress,
} from "../containers/inline-stripe-checkout";
import * as CheckoutServices from "../services/checkout";

type CheckoutKind = "entry" | "plan" | "cart";

type EntryCheckoutContext = { kind: "entry"; entry: Entry };
type PlanCheckoutContext = { kind: "plan"; plan: Plan };
type CartCheckoutContext = { kind: "cart"; items: CartItem[] };

type CheckoutContext =
	| EntryCheckoutContext
	| PlanCheckoutContext
	| CartCheckoutContext;

type Totals = {
	subtotal: number;
	shippingFee?: number;
	discountAmount?: number;
	total: number;
	currency: string;
};

export default function CheckoutPage() {
	const { http } = useHttpProvider();
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams();
	const cart = useCart();
	const { settings: siteSettings } = useSiteSettings();

	const kind = resolveKind(location.pathname);
	const ctx = resolveContext(
		kind,
		params as Record<string, string | undefined>,
		location.state,
		cart.items
	);

	const [clientSecret, setClientSecret] = useState<string>();
	const [totals, setTotals] = useState<Totals | undefined>();
	const [isLoading, setIsLoading] = useState(false);
	const [appliedPromotionCode, setAppliedPromotionCode] = useState<
		string | null
	>(null);
	const [defaultAddress, setDefaultAddress] = useState<
		DefaultAddress | undefined
	>();
	const [isProfileLoaded, setIsProfileLoaded] = useState(false);

	async function fetchPaymentIntent(promotionCode?: string) {
		if (!ctx) return;
		const userId = AuthenticationProvider.user!.id;
		try {
			setIsLoading(true);
			if (ctx.kind === "entry") {
				const result = await CheckoutServices.createEntryPaymentIntent(
					http,
					location.pathname,
					userId,
					ctx.entry.stripePriceId,
					ctx.entry.id,
					{
						value: Number(ctx.entry.price),
						currency: "AUD",
						contentName: `entry_${ctx.entry.count}`,
						contentIds: [`entry_${ctx.entry.id}`],
						numItems: 1,
					}
				);
				setClientSecret(result.clientSecret);
			} else if (ctx.kind === "plan") {
				const result = await CheckoutServices.createSubscriptionPaymentIntent(
					http,
					location.pathname,
					userId,
					ctx.plan.stripePriceId,
					ctx.plan.id,
					{
						value: Number(ctx.plan.price),
						currency: "AUD",
						contentName: ctx.plan.name,
						contentIds: [`plan_${ctx.plan.id}`],
						numItems: 1,
					},
					promotionCode
				);
				setClientSecret(result.clientSecret);
				setTotals({
					subtotal: result.subtotal,
					discountAmount: result.discountAmount,
					total: result.total,
					currency: "AUD",
				});
				setAppliedPromotionCode(result.promotionCodeApplied ?? null);
			} else if (ctx.kind === "cart") {
				const result = await CheckoutServices.createMerchPaymentIntent(
					http,
					location.pathname,
					{
						value: ctx.items.reduce(
							(sum, item) =>
								sum +
								(Number(item.basePrice) + Number(item.priceModifier)) *
									item.quantity,
							0
						),
						currency: "AUD",
						contentIds: ctx.items.map((i) => String(i.variantId)),
						numItems: ctx.items.reduce((sum, i) => sum + i.quantity, 0),
					},
					promotionCode
				);
				setClientSecret(result.clientSecret);
				setTotals({
					subtotal: result.subtotal,
					shippingFee: result.shippingFee,
					discountAmount: result.discountAmount,
					total: result.total,
					currency: result.currency,
				});
				setAppliedPromotionCode(result.promotionCodeApplied ?? null);
			}
		} catch (error: unknown) {
			const detail = (error as { detail?: string })?.detail;
			if (detail === "invalid_promotion_code") {
				notifyError("Coupon invalid", "That code isn't valid or has expired.");
				throw error;
			}
			if (detail === "region_not_allowed") {
				notifyError(
					"Not available in your region",
					"Subscriptions and entries can't be purchased from your region."
				);
				navigate(-1);
				throw error;
			}

			if (detail === "insufficient_stock") {
				notifyError(
					"Stock unavailable",
					"One or more items no longer have enough stock."
				);
				navigate(-1);
			} else if (detail === "empty_cart") {
				notifyError("Cart is empty", "Add items before checking out.");
				navigate(MerchRoute, { replace: true });
			} else {
				notifyError(
					"Checkout failed",
					"Could not start checkout. Please try again."
				);
			}
			throw error;
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		if (!AuthenticationProvider.isAuthenticated) {
			navigate(HomeRoute, { replace: true });
			return;
		}
		if (!ctx) return;

		(async () => {
			try {
				const { profile, address } = await ProfileServices.getProfile(http);
				if (address || profile) {
					setDefaultAddress({
						name: [profile?.firstName, profile?.lastName]
							.filter(Boolean)
							.join(" ") || undefined,
						line1: address?.streetAddress || undefined,
						city: address?.city || undefined,
						state: address?.stateCode ?? undefined,
						postalCode: address?.zipCode || undefined,
						country: "AU",
						phone: profile?.mobileNumber || undefined,
					});
				}
			} catch {
				// non-blocking — checkout still works without prefill
			} finally {
				setIsProfileLoaded(true);
			}
			fetchPaymentIntent().catch(() => undefined);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!ctx) {
		return <MissingContext kind={kind} />;
	}

	function onComplete() {
		notifySuccess("Payment successful", "Your purchase is being processed.");
		if (ctx?.kind === "entry") {
			navigate(ProfileDashboardRoute, { replace: true });
		} else if (ctx?.kind === "plan") {
			navigate(ProfileManageMembershipRoute, { replace: true });
		} else if (ctx?.kind === "cart") {
			cart.clear();
			navigate(MerchRoute, { replace: true });
		} else {
			navigate(HomeRoute, { replace: true });
		}
	}

	function onCancel() {
		navigate(-1);
	}

	async function applyPromotionCode(code: string) {
		try {
			await fetchPaymentIntent(code);
			notifySuccess("Coupon applied", "Discount has been added to your order.");
		} catch {
			// already toasted in fetchPaymentIntent
		}
	}

	async function removePromotionCode() {
		try {
			await fetchPaymentIntent(undefined);
		} catch {
			// already toasted
		}
	}

	return (
		<div className="flex min-h-[calc(100vh-200px)] w-full flex-col gap-8 px-4 py-10 md:px-37 md:py-16">
			<span className="web-heading-h3">Checkout</span>

			<div className="grid gap-8 md:grid-cols-[minmax(0,_1fr)_minmax(0,_1.4fr)]">
				<OrderSummary
					ctx={ctx}
					totals={totals}
					appliedPromotionCode={appliedPromotionCode}
					onApplyCode={
						(ctx.kind === "plan" && siteSettings.subscriptionCouponCodesEnabled) ||
						(ctx.kind === "cart" && siteSettings.merchCouponCodesEnabled)
							? applyPromotionCode
							: undefined
					}
					onRemoveCode={
						(ctx.kind === "plan" && siteSettings.subscriptionCouponCodesEnabled) ||
						(ctx.kind === "cart" && siteSettings.merchCouponCodesEnabled)
							? removePromotionCode
							: undefined
					}
					isLoading={isLoading}
				/>

				<div className="rounded-2xl bg-white p-2 md:p-4">
					{isLoading || !clientSecret || !isProfileLoaded ? (
						<div className="flex h-64 items-center justify-center text-gray-500">
							Loading checkout…
						</div>
					) : (
						<InlineStripeCheckout
							key={clientSecret}
							clientSecret={clientSecret}
							mode={ctx.kind === "plan" ? "subscription" : "payment"}
							addressMode={ctx.kind === "cart" ? "shipping" : "billing"}
							defaultAddress={defaultAddress}
							confirmLabel={confirmLabelFor(ctx, totals)}
							cancelLabel="Cancel"
							onComplete={onComplete}
							onCancel={onCancel}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

function resolveKind(pathname: string): CheckoutKind | null {
	if (pathname.startsWith("/checkout/entry/")) return "entry";
	if (pathname.startsWith("/checkout/plan/")) return "plan";
	if (pathname === "/checkout/cart") return "cart";
	return null;
}

function resolveContext(
	kind: CheckoutKind | null,
	params: Record<string, string | undefined>,
	state: unknown,
	cartItems: CartItem[]
): CheckoutContext | null {
	if (kind === "entry") {
		const entry = (state as { entry?: Entry } | null)?.entry;
		if (!entry) return null;
		const idFromUrl = Number(params.entryId);
		if (entry.id !== idFromUrl) return null;
		return { kind: "entry", entry };
	}
	if (kind === "plan") {
		const plan = (state as { plan?: Plan } | null)?.plan;
		if (!plan) return null;
		const idFromUrl = Number(params.planId);
		if (plan.id !== idFromUrl) return null;
		return { kind: "plan", plan };
	}
	if (kind === "cart") {
		if (!cartItems || cartItems.length === 0) return null;
		return { kind: "cart", items: cartItems };
	}
	return null;
}

function confirmLabelFor(ctx: CheckoutContext, totals?: Totals): string {
	if (ctx.kind === "entry") return `Pay $${Number(ctx.entry.price).toFixed(2)}`;
	if (ctx.kind === "plan") {
		if (totals) return `Pay ${formatPrice(totals.total, totals.currency)}`;
		return "Pay & subscribe";
	}
	if (ctx.kind === "cart" && totals)
		return `Pay ${formatPrice(totals.total, totals.currency)}`;
	return "Pay";
}

function OrderSummary({
	ctx,
	totals,
	appliedPromotionCode,
	onApplyCode,
	onRemoveCode,
	isLoading,
}: {
	ctx: CheckoutContext;
	totals?: Totals;
	appliedPromotionCode: string | null;
	onApplyCode?: (code: string) => Promise<void>;
	onRemoveCode?: () => Promise<void>;
	isLoading: boolean;
}) {
	if (ctx.kind === "entry") {
		const total = `$${Number(ctx.entry.price).toFixed(2)}`;
		const label = `${ctx.entry.count} ${
			ctx.entry.count === 1 ? "entry" : "entries"
		}`;
		return (
			<SummaryCard>
				<span className="web-heading-h4">Order summary</span>
				<div className="flex items-center justify-between">
					<span className="web-body-b1">{label}</span>
					<span className="web-body-b1-medium">{total}</span>
				</div>
				<div className="border-t pt-4 flex items-center justify-between">
					<span className="web-body-b1-medium">Total</span>
					<span className="web-heading-h4">{total} AUD</span>
				</div>
			</SummaryCard>
		);
	}
	if (ctx.kind === "plan") {
		const subtotalLabel = `$${Number(ctx.plan.price).toFixed(2)}`;
		return (
			<SummaryCard>
				<span className="web-heading-h4">Order summary</span>
				<div className="flex items-center justify-between">
					<span className="web-body-b1">{ctx.plan.name}</span>
					<span className="web-body-b1-medium">{subtotalLabel} / month</span>
				</div>
				<p className="web-body-b3 text-gray-500">
					Cancel anytime from your profile.
				</p>

				{onApplyCode && (
					<CouponField
						applied={appliedPromotionCode}
						isLoading={isLoading}
						onApply={onApplyCode}
						onRemove={onRemoveCode}
					/>
				)}

				{totals && (totals.discountAmount ?? 0) > 0 && (
					<div className="flex items-center justify-between text-green-700">
						<span className="web-body-b1">Discount</span>
						<span className="web-body-b1-medium">
							−{formatPrice(totals.discountAmount ?? 0, totals.currency)}
						</span>
					</div>
				)}

				<div className="border-t pt-4 flex items-center justify-between">
					<span className="web-body-b1-medium">Total today</span>
					<span className="web-heading-h4">
						{totals ? formatPrice(totals.total, totals.currency) : subtotalLabel}{" "}
						AUD
					</span>
				</div>
			</SummaryCard>
		);
	}
	if (ctx.kind === "cart") {
		return (
			<SummaryCard>
				<span className="web-heading-h4">Order summary</span>
				<div className="flex flex-col gap-3">
					{ctx.items.map((item) => {
						const unit =
							Number(item.basePrice ?? 0) + Number(item.priceModifier ?? 0);
						return (
							<div
								key={item.variantId}
								className="flex items-start justify-between gap-3"
							>
								<div className="min-w-0">
									<p className="web-body-b1-medium truncate">
										{item.productName}
									</p>
									<p className="web-body-b3 text-gray-700">
										{item.variantName} · ×{item.quantity}
									</p>
								</div>
								<span className="web-body-b1-medium">
									{formatPrice(unit * item.quantity, "AUD")}
								</span>
							</div>
						);
					})}
				</div>
				<div className="flex items-center justify-between border-t pt-4">
					<span className="web-body-b1 text-gray-700">Subtotal</span>
					<span className="web-body-b1-medium">
						{totals ? formatPrice(totals.subtotal, totals.currency) : "—"}
					</span>
				</div>

				{onApplyCode && (
					<CouponField
						applied={appliedPromotionCode}
						isLoading={isLoading}
						onApply={onApplyCode}
						onRemove={onRemoveCode}
					/>
				)}

				{totals && (totals.discountAmount ?? 0) > 0 && (
					<div className="flex items-center justify-between text-green-700">
						<span className="web-body-b1">Discount</span>
						<span className="web-body-b1-medium">
							−{formatPrice(totals.discountAmount ?? 0, totals.currency)}
						</span>
					</div>
				)}

				<div className="flex items-center justify-between">
					<span className="web-body-b1 text-gray-700">Shipping</span>
					<span className="web-body-b1-medium">
						{totals
							? (totals.shippingFee ?? 0) === 0
								? "Free"
								: formatPrice(totals.shippingFee ?? 0, totals.currency)
							: "—"}
					</span>
				</div>
				<div className="flex items-center justify-between border-t pt-4">
					<span className="web-body-b1-medium">Total</span>
					<span className="web-heading-h4">
						{totals ? formatPrice(totals.total, totals.currency) : "—"}
					</span>
				</div>
			</SummaryCard>
		);
	}
	return null;
}

function SummaryCard({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-4 rounded-2xl bg-light p-6">{children}</div>
	);
}

export function CouponField({
	applied,
	isLoading,
	onApply,
	onRemove,
}: {
	applied: string | null;
	isLoading: boolean;
	onApply: (code: string) => Promise<void>;
	onRemove?: () => Promise<void>;
}) {
	const [code, setCode] = useState("");

	if (applied) {
		return (
			<div className="flex items-center justify-between rounded-xl bg-white px-4 py-3">
				<div className="flex flex-col">
					<span className="web-body-b3 text-gray-500">Coupon applied</span>
					<span className="web-body-b1-medium">{applied}</span>
				</div>
				<button
					type="button"
					disabled={isLoading}
					onClick={() => onRemove?.()}
					className="web-body-b3 text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline disabled:opacity-50"
				>
					Remove
				</button>
			</div>
		);
	}

	return (
		<div className="flex gap-2">
			<Input
				value={code}
				onChange={(e) => setCode(e.target.value)}
				placeholder="Coupon code"
				className="bg-white"
				disabled={isLoading}
			/>
			<Button
				type="button"
				variant="secondary"
				disabled={isLoading || !code.trim()}
				onClick={() => onApply(code.trim())}
			>
				Apply
			</Button>
		</div>
	);
}

function MissingContext({ kind }: { kind: CheckoutKind | null }) {
	const navigate = useNavigate();
	useEffect(() => {
		notifyError(
			"Checkout unavailable",
			"Please pick an item to checkout first."
		);
		const timer = window.setTimeout(() => {
			if (kind === "entry") navigate(GiveawayDetailsRoute, { replace: true });
			else if (kind === "cart") navigate(MerchRoute, { replace: true });
			else navigate(HomeRoute, { replace: true });
		}, 800);
		return () => window.clearTimeout(timer);
	}, [kind, navigate]);
	return (
		<div className="flex min-h-[calc(100vh-200px)] items-center justify-center text-gray-500">
			Redirecting…
		</div>
	);
}
